/* Executive Penthouse Suite & Lounge (js/penthouse.js)
   Redesigned:
   - NYC-style Exposed Red Brick Accent Back Wall with authentic mortar joints & depth
   - Deep Royal Wine / Crimson Red Executive Carpet with golden border inlay
   - Flanking lush architectural floor plants (monstera / fiddle leaf ficus style) with ceramic planters & gold bases
   - Executive dark walnut conference table with brass chamfer, table lamp & steaming coffee
   - Modern luxury club armchairs & animated gesturing executive avatar
   - High-end architectural warm golden ceiling chandelier & ambient flood lighting
*/

import * as THREE from "three";

/** Create authentic NYC-style exposed red brick texture on canvas */
function makeNYCBrickTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d");

  // Mortar base (dark dusty vintage grout)
  ctx.fillStyle = "#2c2624";
  ctx.fillRect(0, 0, 1024, 1024);

  // Add subtle mortar noise/grit
  for (let i = 0; i < 8000; i++) {
    const nx = Math.random() * 1024;
    const ny = Math.random() * 1024;
    ctx.fillStyle =
      Math.random() > 0.5 ? "rgba(70,60,55,0.4)" : "rgba(20,18,16,0.5)";
    ctx.fillRect(nx, ny, 2, 2);
  }

  const rows = 32;
  const rowHeight = 1024 / rows;
  const mortarThickness = 4;
  const brickWidth = 80;

  // Palette of authentic NYC weathered terracotta/crimson/burnt sienna red brick tones (darker, atmospheric tones)
  const brickColors = [
    "#591612",
    "#6b1b1a",
    "#4e110e",
    "#6d2315",
    "#420e0a",
    "#561914",
    "#3d0c08",
    "#5e1b13",
    "#722818",
    "#4a130f",
  ];

  for (let r = 0; r < rows; r++) {
    const y = r * rowHeight + mortarThickness / 2;
    const h = rowHeight - mortarThickness;
    // Stagger alternating rows by half a brick (running bond brick pattern)
    const offsetX = r % 2 === 0 ? 0 : -brickWidth / 2;

    for (
      let x = offsetX - brickWidth;
      x < 1024 + brickWidth;
      x += brickWidth + mortarThickness
    ) {
      // Pick random brick shade
      const baseColor =
        brickColors[Math.floor(Math.random() * brickColors.length)];
      ctx.fillStyle = baseColor;

      // Rounded slightly weathered corners
      const bx = Math.max(0, x);
      const bw = Math.min(1024 - bx, x + brickWidth - bx);
      if (bw <= 0) continue;

      ctx.fillRect(bx, y, bw, h);

      // Add surface texture / flecks / burn marks across individual brick
      const flecks = Math.floor(Math.random() * 8) + 4;
      for (let f = 0; f < flecks; f++) {
        const fx = bx + Math.random() * bw;
        const fy = y + Math.random() * h;
        const fSize = Math.random() * 8 + 3;
        ctx.fillStyle =
          Math.random() > 0.4
            ? "rgba(35, 12, 10, 0.35)"
            : "rgba(220, 110, 80, 0.25)";
        ctx.fillRect(fx, fy, fSize, fSize * 0.6);
      }

      // Top edge highlight & bottom edge shadow for 3D depth
      ctx.fillStyle = "rgba(255, 200, 180, 0.12)";
      ctx.fillRect(bx, y, bw, 2);
      ctx.fillStyle = "rgba(0, 0, 0, 0.35)";
      ctx.fillRect(bx, y + h - 2, bw, 2);
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 2);
  return texture;
}

/** Create luxury Persian / executive red rug texture with elegant gold border */
function makeRedCarpetTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");

  // Rich deep crimson red velvet base
  ctx.fillStyle = "#800f1c";
  ctx.fillRect(0, 0, 512, 512);

  // Subtle fabric weave texture
  for (let i = 0; i < 6000; i++) {
    const rx = Math.random() * 512;
    const ry = Math.random() * 512;
    ctx.fillStyle =
      Math.random() > 0.5
        ? "rgba(100, 10, 20, 0.3)"
        : "rgba(160, 25, 40, 0.25)";
    ctx.fillRect(rx, ry, 2, 2);
  }

  // Outer border - dark wine
  ctx.lineWidth = 14;
  ctx.strokeStyle = "#4d0610";
  ctx.strokeRect(12, 12, 488, 488);

  // Elegant gold leaf accent border line
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#d4af37";
  ctx.strokeRect(28, 28, 456, 456);

  // Inner thin gold pinstripe
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(212, 175, 55, 0.6)";
  ctx.strokeRect(38, 38, 436, 436);

  // Inner decorative corners
  const corners = [
    [48, 48],
    [464, 48],
    [48, 464],
    [464, 464],
  ];
  ctx.fillStyle = "#d4af37";
  corners.forEach(([cx, cy]) => {
    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    ctx.fill();
  });

  const texture = new THREE.CanvasTexture(canvas);
  return texture;
}

/** Create coffee steam texture */
function makeSteamTexture() {
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 128;
  const g = c.getContext("2d");
  const grad = g.createLinearGradient(32, 128, 32, 0);
  grad.addColorStop(0, "rgba(255, 255, 255, 0.5)");
  grad.addColorStop(0.5, "rgba(255, 255, 255, 0.25)");
  grad.addColorStop(1, "rgba(255, 255, 255, 0)");
  g.fillStyle = grad;
  g.beginPath();
  g.ellipse(32, 64, 16, 50, 0, 0, Math.PI * 2);
  g.fill();
  const tex = new THREE.CanvasTexture(c);
  return tex;
}

/** Create a modern luxury armchair */
function makeLoungeChair(x, z, rotY) {
  const chair = new THREE.Group();
  chair.position.set(x, -5.6, z);
  chair.rotation.y = rotY;

  const leatherMat = new THREE.MeshStandardMaterial({
    color: 0x221a16, // rich dark espresso leather
    roughness: 0.5,
    metalness: 0.1,
  });
  const cushionMat = new THREE.MeshStandardMaterial({
    color: 0x382c24, // warm saddle tone cushion
    roughness: 0.6,
    metalness: 0.05,
  });
  const brassMat = new THREE.MeshStandardMaterial({
    color: 0xd4af37, // warm brass legs
    metalness: 0.85,
    roughness: 0.25,
  });

  // Seat base & cushion
  const seatBase = new THREE.Mesh(
    new THREE.BoxGeometry(0.9, 0.14, 0.85),
    leatherMat,
  );
  seatBase.position.y = 0.5;
  const cushion = new THREE.Mesh(
    new THREE.BoxGeometry(0.82, 0.12, 0.78),
    cushionMat,
  );
  cushion.position.y = 0.62;

  // Backrest curved / angled
  const backrest = new THREE.Mesh(
    new THREE.BoxGeometry(0.88, 0.8, 0.14),
    leatherMat,
  );
  backrest.position.set(0, 0.95, -0.38);
  backrest.rotation.x = -0.08;

  // Armrests (Left & Right)
  const armGeo = new THREE.BoxGeometry(0.12, 0.35, 0.8);
  const armL = new THREE.Mesh(armGeo, leatherMat);
  armL.position.set(-0.46, 0.72, -0.02);
  const armR = new THREE.Mesh(armGeo, leatherMat);
  armR.position.set(0.46, 0.72, -0.02);

  // 4 Slanted brass legs
  for (const [lx, lz] of [
    [-0.38, -0.32],
    [0.38, -0.32],
    [-0.38, 0.32],
    [0.38, 0.32],
  ]) {
    const leg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.02, 0.015, 0.48, 8),
      brassMat,
    );
    leg.position.set(lx, 0.24, lz);
    chair.add(leg);
  }

  chair.add(seatBase, cushion, backrest, armL, armR);
  return chair;
}

/** Create stylized faceless executive 3D avatar seated in chair gesturing/talking */
function makeExecutiveAvatar() {
  const avatar = new THREE.Group();

  // Materials - sleek minimal faceless mannequin / executive aesthetic
  const suitMat = new THREE.MeshStandardMaterial({
    color: 0x182030, // midnight navy executive tailored suit
    roughness: 0.55,
    metalness: 0.15,
  });
  const mannequinMat = new THREE.MeshStandardMaterial({
    color: 0xe2e8f0, // sleek matte silver/porcelain faceless mannequin head & hands
    roughness: 0.35,
    metalness: 0.25,
  });

  // 1. Seated Torso & Shoulders (clean minimalist tailored suit)
  const torso = new THREE.Mesh(
    new THREE.BoxGeometry(0.72, 0.82, 0.42),
    suitMat,
  );
  torso.position.set(0, 0.44, 0);

  // 2. Faceless Minimal Head & Neck (smooth modern geometric mannequin)
  const headGroup = new THREE.Group();
  headGroup.position.set(0, 1.05, 0.02);

  const neck = new THREE.Mesh(
    new THREE.CylinderGeometry(0.09, 0.11, 0.18, 14),
    mannequinMat,
  );
  neck.position.y = -0.06;
  headGroup.add(neck);

  // Smooth faceless head
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.25, 20, 18),
    mannequinMat,
  );
  head.scale.set(0.9, 1.2, 0.95);
  head.position.y = 0.15;
  headGroup.add(head);

  // 3. Left Arm (resting comfortably on table/armrest, relaxed)
  const armGeo = new THREE.CapsuleGeometry(0.08, 0.42, 4, 8);
  armGeo.translate(0, -0.2, 0);

  const armL = new THREE.Mesh(armGeo, suitMat);
  armL.position.set(-0.42, 0.72, 0.05);
  armL.rotation.set(0.85, -0.15, 0.2);

  const forearmL = new THREE.Mesh(armGeo.clone(), suitMat);
  forearmL.position.set(0, -0.38, 0);
  forearmL.rotation.set(-1.0, 0, -0.2);
  armL.add(forearmL);

  const handL = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 10, 10),
    mannequinMat,
  );
  handL.scale.set(1.0, 0.7, 1.4);
  handL.position.set(0, -0.38, 0.04);
  forearmL.add(handL);

  // 4. Right Arm (Active expressive talking / gesturing arm)
  const armR = new THREE.Mesh(armGeo.clone(), suitMat);
  armR.position.set(0.42, 0.72, 0.05);
  armR.rotation.set(0.75, 0.2, -0.25);

  const forearmR = new THREE.Mesh(armGeo.clone(), suitMat);
  forearmR.position.set(0, -0.38, 0);
  forearmR.rotation.set(-1.25, 0.2, 0.4);
  armR.add(forearmR);

  const handR = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 10, 10),
    mannequinMat,
  );
  handR.scale.set(1.0, 0.7, 1.4);
  handR.position.set(0, -0.38, 0.04);
  forearmR.add(handR);

  // 5. Seated Upper Legs
  const legGeo = new THREE.BoxGeometry(0.24, 0.22, 0.65);
  const legL = new THREE.Mesh(legGeo, suitMat);
  legL.position.set(-0.2, 0.12, 0.3);
  const legR = new THREE.Mesh(legGeo, suitMat);
  legR.position.set(0.2, 0.12, 0.3);

  avatar.add(torso, headGroup, armL, armR, legL, legR);
  avatar.userData = { headGroup, armR, forearmR, armL };

  return avatar;
}

/** Create potted architectural bonsai/plant */
function makeIndoorPlant() {
  const plantGroup = new THREE.Group();

  // Ceramic pot
  const potMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    roughness: 0.3,
    metalness: 0.2,
  });
  const pot = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.12, 0.22, 16),
    potMat,
  );
  pot.position.y = 0.11;
  plantGroup.add(pot);

  // Soil
  const soil = new THREE.Mesh(
    new THREE.CylinderGeometry(0.16, 0.16, 0.02, 16),
    new THREE.MeshStandardMaterial({ color: 0x1a120b, roughness: 0.9 }),
  );
  soil.position.y = 0.21;
  plantGroup.add(soil);

  // Stems & Leaves
  const leafMat = new THREE.MeshStandardMaterial({
    color: 0x15803d, // vibrant lush green
    roughness: 0.6,
    metalness: 0.1,
  });
  const stemMat = new THREE.MeshStandardMaterial({
    color: 0x3f2e1d,
    roughness: 0.8,
  });

  const stemGeo = new THREE.CylinderGeometry(0.015, 0.02, 0.28, 6);
  const stem = new THREE.Mesh(stemGeo, stemMat);
  stem.position.y = 0.32;
  plantGroup.add(stem);

  // Organic leafy clusters
  const leafAngles = [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3];
  leafAngles.forEach((angle, i) => {
    const leafMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 7, 7),
      leafMat,
    );
    leafMesh.scale.set(1.4, 0.4, 0.9);
    leafMesh.position.set(
      Math.cos(angle) * 0.14,
      0.42 + (i % 2) * 0.06,
      Math.sin(angle) * 0.14,
    );
    leafMesh.rotation.y = angle;
    leafMesh.rotation.z = 0.25;
    plantGroup.add(leafMesh);
  });

  return plantGroup;
}

/** Create large luxury lush floor plant (Fiddle Leaf Fig / Monstera style) */
function makeFloorPlant() {
  const plantGroup = new THREE.Group();

  // Premium matte black ceramic cylindrical planter with brushed gold base ring
  const potMat = new THREE.MeshStandardMaterial({
    color: 0x18181b, // sleek dark zinc planter
    roughness: 0.25,
    metalness: 0.15,
  });
  const goldBaseMat = new THREE.MeshStandardMaterial({
    color: 0xd4af37, // brushed gold base stand
    roughness: 0.3,
    metalness: 0.85,
  });

  // Main tall ceramic pot
  const pot = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.42, 1.1, 24),
    potMat,
  );
  pot.position.y = 0.65;
  plantGroup.add(pot);

  // Gold base accent ring
  const baseRing = new THREE.Mesh(
    new THREE.CylinderGeometry(0.48, 0.48, 0.12, 24),
    goldBaseMat,
  );
  baseRing.position.y = 0.06;
  plantGroup.add(baseRing);

  // Dark organic potting soil
  const soil = new THREE.Mesh(
    new THREE.CylinderGeometry(0.52, 0.52, 0.05, 24),
    new THREE.MeshStandardMaterial({ color: 0x140f0c, roughness: 0.95 }),
  );
  soil.position.y = 1.18;
  plantGroup.add(soil);

  // Materials for stems and vibrant lush green foliage
  const trunkMat = new THREE.MeshStandardMaterial({
    color: 0x422a1d, // natural wood bark
    roughness: 0.85,
  });
  const leafMatLush1 = new THREE.MeshStandardMaterial({
    color: 0x16a34a, // vibrant rich emerald green
    roughness: 0.45,
    metalness: 0.05,
    side: THREE.DoubleSide,
  });
  const leafMatLush2 = new THREE.MeshStandardMaterial({
    color: 0x15803d, // deep forest green
    roughness: 0.5,
    metalness: 0.05,
    side: THREE.DoubleSide,
  });

  // Multiple upward organic stalks
  const stalkConfigs = [
    { height: 2.6, rad: 0.05, angle: 0.2, rotY: 0 },
    { height: 2.2, rad: 0.045, angle: -0.25, rotY: 1.8 },
    { height: 2.9, rad: 0.055, angle: 0.15, rotY: 3.6 },
    { height: 1.8, rad: 0.04, angle: 0.3, rotY: 5.0 },
  ];

  stalkConfigs.forEach((cfg) => {
    const stalk = new THREE.Mesh(
      new THREE.CylinderGeometry(cfg.rad * 0.6, cfg.rad, cfg.height, 8),
      trunkMat,
    );
    stalk.position.set(
      Math.sin(cfg.rotY) * 0.12,
      1.1 + cfg.height / 2,
      Math.cos(cfg.rotY) * 0.12,
    );
    stalk.rotation.z = cfg.angle;
    stalk.rotation.y = cfg.rotY;
    plantGroup.add(stalk);
  });

  // Large fan/oval tropical leaves spreading outward at staggered heights
  const leafCount = 14;
  for (let i = 0; i < leafCount; i++) {
    const leafGroup = new THREE.Group();
    const leafMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.48, 8, 8),
      i % 2 === 0 ? leafMatLush1 : leafMatLush2,
    );
    // Flatten into broad organic leaf shape
    leafMesh.scale.set(1.6, 0.12, 0.85);
    leafMesh.position.set(0.45, 0, 0);

    const leafStem = new THREE.Mesh(
      new THREE.CylinderGeometry(0.015, 0.02, 0.45, 6),
      trunkMat,
    );
    leafStem.rotation.z = Math.PI / 2;
    leafStem.position.set(0.2, 0, 0);

    leafGroup.add(leafStem, leafMesh);

    // Position around perimeter at various heights
    const angle = (i / leafCount) * Math.PI * 2 + (i % 3) * 0.2;
    const height = 1.6 + (i / leafCount) * 1.8;
    const tilt = 0.35 + (i % 4) * 0.08;

    leafGroup.position.set(
      Math.cos(angle) * 0.22,
      height,
      Math.sin(angle) * 0.22,
    );
    leafGroup.rotation.y = angle;
    leafGroup.rotation.z = tilt;
    plantGroup.add(leafGroup);
  }

  return plantGroup;
}

export function buildPenthouse(scene) {
  const phGroup = new THREE.Group();
  // Penthouse center coordinate inside the skyscraper (Y: 36.0, Z: 0)
  phGroup.position.set(0, 36.0, 0);

  // 1. Dark executive hardwood parquet floor spanning full 14.8x14.8 footprint (aligned with building exterior 15x15)
  // Positioned at y: -5.7 (top surface at -5.6) so it sits safely above slab3 at Y: 30 (y: -6.0 local, top at -5.7).
  const floorGeo = new THREE.BoxGeometry(14.8, 0.2, 14.8);
  const floorMat = new THREE.MeshStandardMaterial({
    color: 0x0a0a0a, // Pure deep black luxury parquet floor
    roughness: 0.25,
    metalness: 0.15,
  });
  const floorMesh = new THREE.Mesh(floorGeo, floorMat);
  floorMesh.position.set(0, -5.7, 0);
  phGroup.add(floorMesh);

  // 2. Luxury Royal Red Designer Velvet Rug with Gold Border Inlay
  const rugTex = makeRedCarpetTexture();
  const rugGeo = new THREE.PlaneGeometry(9.2, 7.2);
  const rugMat = new THREE.MeshStandardMaterial({
    map: rugTex,
    roughness: 0.85,
    metalness: 0.1,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
  const rug = new THREE.Mesh(rugGeo, rugMat);
  rug.rotation.x = -Math.PI / 2;
  // Positioned at y: -5.58 (clearly above the hardwood floor top at -5.6)
  rug.position.set(0, -5.58, 0);
  phGroup.add(rug);

  // 2b. NYC-Style Exposed Red Brick Accent Back Wall (darker, atmospheric matte tone)
  const brickTex = makeNYCBrickTexture();
  const brickMat = new THREE.MeshStandardMaterial({
    map: brickTex,
    color: 0x888888, // slightly tinted down to avoid over-exposure from room lights
    roughness: 0.95,
    metalness: 0.02,
    bumpMap: brickTex,
    bumpScale: 0.035,
  });
  // Positioned flush along the back wall (Z = -7.28, just in front of back glass & mullions)
  const backBrickWall = new THREE.Mesh(
    new THREE.BoxGeometry(14.6, 11.5, 0.15),
    brickMat,
  );
  backBrickWall.position.set(0, 0.0, -7.28);
  phGroup.add(backBrickWall);

  // Subtle, moody warm accent wash on the brick wall
  const brickWallWashLight = new THREE.PointLight(0xd97736, 1.2, 9, 1.6);
  brickWallWashLight.position.set(0, -3.8, -6.0);
  phGroup.add(brickWallWashLight);

  // 2c. Lush Green Architectural Floor Plants on Left and Right sides
  const plantLeftFront = makeFloorPlant();
  plantLeftFront.position.set(-5.6, -5.7, 1.8);
  const plantLeftBack = makeFloorPlant();
  plantLeftBack.position.set(-5.6, -5.7, -4.2);
  plantLeftBack.rotation.y = 1.2;

  const plantRightFront = makeFloorPlant();
  plantRightFront.position.set(5.6, -5.7, 1.8);
  plantRightFront.rotation.y = -0.8;
  const plantRightBack = makeFloorPlant();
  plantRightBack.position.set(5.6, -5.7, -4.2);
  plantRightBack.rotation.y = 2.4;

  phGroup.add(plantLeftFront, plantLeftBack, plantRightFront, plantRightBack);

  // 3. Executive Business Table (Solid Italian Walnut + Brushed Gold Chamfer & Sleek Architectural Legs)
  const tableMat = new THREE.MeshStandardMaterial({
    color: 0x24150d, // rich deep executive walnut
    roughness: 0.25,
    metalness: 0.1,
  });
  const tableGoldMat = new THREE.MeshStandardMaterial({
    color: 0xd4af37, // brushed gold / brass accent trim
    metalness: 0.85,
    roughness: 0.2,
  });

  // Rectangular Table top
  const tableTop = new THREE.Mesh(
    new THREE.BoxGeometry(4.0, 0.12, 2.2),
    tableMat,
  );
  tableTop.position.set(0, -4.7, 0);
  phGroup.add(tableTop);

  // Gold inlay perimeter frame around top edge
  const tableGoldRim = new THREE.Mesh(
    new THREE.BoxGeometry(4.06, 0.04, 2.26),
    tableGoldMat,
  );
  tableGoldRim.position.set(0, -4.75, 0);
  phGroup.add(tableGoldRim);

  // 4 Sleek modern architectural rectangular legs with brass tips
  const legGeo = new THREE.BoxGeometry(0.12, 1.0, 0.12);
  const legTipGeo = new THREE.BoxGeometry(0.13, 0.18, 0.13);
  for (const [lx, lz] of [
    [-1.75, -0.88],
    [1.75, -0.88],
    [-1.75, 0.88],
    [1.75, 0.88],
  ]) {
    const leg = new THREE.Mesh(legGeo, tableMat);
    leg.position.set(lx, -5.25, lz);
    const legTip = new THREE.Mesh(legTipGeo, tableGoldMat);
    legTip.position.set(0, -0.42, 0);
    leg.add(legTip);
    phGroup.add(leg);
  }

  // 4. Beautiful Cozy Tabletop Lamp
  const lampGroup = new THREE.Group();
  lampGroup.position.set(-1.2, -4.64, -0.45);

  // Lamp brass base & slender rod
  const lampBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.09, 0.12, 0.04, 16),
    tableGoldMat,
  );
  const lampRod = new THREE.Mesh(
    new THREE.CylinderGeometry(0.015, 0.015, 0.45, 12),
    tableGoldMat,
  );
  lampRod.position.y = 0.24;

  // Frosted warm luminous glass shade
  const shadeMat = new THREE.MeshStandardMaterial({
    color: 0xfff2cc,
    emissive: 0xffaa33,
    emissiveIntensity: 1.8,
    roughness: 0.2,
    metalness: 0.0,
    transparent: true,
    opacity: 0.92,
  });
  const lampShade = new THREE.Mesh(
    new THREE.ConeGeometry(0.18, 0.22, 16, 1, true),
    shadeMat,
  );
  lampShade.position.y = 0.46;

  lampGroup.add(lampBase, lampRod, lampShade);
  phGroup.add(lampGroup);

  // Point light radiating directly from table lamp
  const tableLampLight = new THREE.PointLight(0xffb84d, 5.0, 6, 1.4);
  tableLampLight.position.set(-1.2, -4.2, -0.45);
  phGroup.add(tableLampLight);

  // 5. Potted Plant on Table
  const tablePlant = makeIndoorPlant();
  tablePlant.position.set(1.1, -4.64, -0.35);
  phGroup.add(tablePlant);

  // 6. Ceramic Coffee Mug with Fresh Floating Steam
  const mugMat = new THREE.MeshStandardMaterial({
    color: 0x0284c7, // SmartFlow blue ceramic mug
    roughness: 0.3,
  });
  const mug = new THREE.Mesh(
    new THREE.CylinderGeometry(0.07, 0.055, 0.12, 14),
    mugMat,
  );
  mug.position.set(0.0, -4.58, 0.35);

  const handleGeo = new THREE.TorusGeometry(0.04, 0.014, 6, 12, Math.PI * 1.3);
  const handle = new THREE.Mesh(handleGeo, mugMat);
  handle.position.set(0.08, 0, 0);
  handle.rotation.z = -Math.PI / 2;
  mug.add(handle);
  phGroup.add(mug);

  // Coffee steam plane
  const steamTex = makeSteamTexture();
  const steamMat = new THREE.MeshBasicMaterial({
    map: steamTex,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
    toneMapped: false,
  });
  const steamMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.15, 0.38),
    steamMat,
  );
  steamMesh.position.set(0.0, -4.3, 0.35);
  phGroup.add(steamMesh);

  // 7. Luxury Executive Armchairs & Seating Arrangement
  // Table size is 4.0 (X) by 2.2 (Z). Half sizes: X=2.0, Z=1.1.
  // Chair C: Head/Executive chair facing towards table and camera (+Z)
  const chairC = makeLoungeChair(0.0, -1.95, 0);

  // Stylized faceless executive 3D avatar seated comfortably in the head chair
  const avatar = makeExecutiveAvatar();
  // Place avatar on the seat of chair C
  avatar.position.set(0, 0.48, 0.05);
  chairC.add(avatar);

  // Guest Chairs along the table:
  // Side chairs placed well outside table edge (|x| = 2.75 > 2.0 table width)
  const chairLeft = makeLoungeChair(-2.75, 0.0, Math.PI / 2);
  const chairRight = makeLoungeChair(2.75, 0.0, -Math.PI / 2);

  // 2 Front Guest Chairs facing the executive across the table (z = 1.95 > 1.1 table depth)
  const chairFrontL = makeLoungeChair(-1.15, 1.95, Math.PI);
  const chairFrontR = makeLoungeChair(1.15, 1.95, Math.PI);

  phGroup.add(chairC, chairLeft, chairRight, chairFrontL, chairFrontR);

  // 8. High-End Grand Architectural Ceiling Ring Chandelier (Raised to y: 4.8)
  const ceilingRingGeo = new THREE.TorusGeometry(3.2, 0.05, 12, 48);
  const goldNeonMat = new THREE.MeshBasicMaterial({
    color: 0xffd166,
    toneMapped: false,
  });
  const ceilingRing = new THREE.Mesh(ceilingRingGeo, goldNeonMat);
  ceilingRing.rotation.x = Math.PI / 2;
  ceilingRing.position.set(0, 4.8, 0);
  phGroup.add(ceilingRing);

  // Inner accent warm halo ring
  const innerRingGeo = new THREE.TorusGeometry(1.6, 0.035, 12, 36);
  const innerRing = new THREE.Mesh(innerRingGeo, goldNeonMat);
  innerRing.rotation.x = Math.PI / 2;
  innerRing.position.set(0, 5.1, 0);
  phGroup.add(innerRing);

  // 9. Panoramic Architectural Floor-to-Ceiling Glass Window Grids (15x15 footprint, 11.6h)
  // Perfectly matching the exterior building structural envelope with elegant floor-to-ceiling panoramic glass divisions
  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x0a0a0a, // Pure jet black architectural frame
    metalness: 0.85,
    roughness: 0.25,
  });

  const H_WALL = 11.6;
  const HALF_W = 7.35; // perimeter glass boundary (exterior is 7.5)

  // 4 Main Structural Corner Columns
  const cornerColGeo = new THREE.BoxGeometry(0.35, H_WALL, 0.35);
  for (const [cx, cz] of [
    [-HALF_W, -HALF_W],
    [HALF_W, -HALF_W],
    [-HALF_W, HALF_W],
    [HALF_W, HALF_W],
  ]) {
    const col = new THREE.Mesh(cornerColGeo, frameMat);
    col.position.set(cx, 0.0, cz);
    phGroup.add(col);
  }

  // Elegant architectural glass window divisions (3 bays per facade)
  // Vertical mullions at 1/3 and 2/3 of each 15m facade
  const vertMullionGeo = new THREE.BoxGeometry(0.12, H_WALL, 0.12);
  const bayOffsets = [-2.45, 2.45];

  // North & South glass walls (Z = ±HALF_W)
  bayOffsets.forEach((bx) => {
    const mN = new THREE.Mesh(vertMullionGeo, frameMat);
    mN.position.set(bx, 0.0, -HALF_W);
    const mS = new THREE.Mesh(vertMullionGeo, frameMat);
    mS.position.set(bx, 0.0, HALF_W);
    phGroup.add(mN, mS);
  });

  // East & West glass walls (X = ±HALF_W)
  bayOffsets.forEach((bz) => {
    const mW = new THREE.Mesh(vertMullionGeo, frameMat);
    mW.position.set(-HALF_W, 0.0, bz);
    const mE = new THREE.Mesh(vertMullionGeo, frameMat);
    mE.position.set(HALF_W, 0.0, bz);
    phGroup.add(mW, mE);
  });

  // Architectural Horizontal Glass Transom Rails (mid-height & ceiling/floor headers)
  const railHGeo = new THREE.BoxGeometry(HALF_W * 2, 0.12, 0.12);
  const railVGeo = new THREE.BoxGeometry(0.12, 0.12, HALF_W * 2);

  // Mid transom rail (y: 0.2 - waist/chest level) and top header rail (y: 5.75)
  for (const ry of [-5.75, 0.2, 5.75]) {
    // North & South
    const rN = new THREE.Mesh(railHGeo, frameMat);
    rN.position.set(0, ry, -HALF_W);
    const rS = new THREE.Mesh(railHGeo, frameMat);
    rS.position.set(0, ry, HALF_W);
    // East & West
    const rW = new THREE.Mesh(railVGeo, frameMat);
    rW.position.set(-HALF_W, ry, 0);
    const rE = new THREE.Mesh(railVGeo, frameMat);
    rE.position.set(HALF_W, ry, 0);
    phGroup.add(rN, rS, rW, rE);
  }

  // 10. Penthouse Warm Architectural & Ceiling Flood Lighting
  // Ceiling Warm Golden Chandelier Wash
  const warmCeilingLight = new THREE.PointLight(0xffea88, 22.0, 36, 1.1);
  warmCeilingLight.position.set(0, 4.5, 0);
  phGroup.add(warmCeilingLight);

  // Warm Ambient Downlight focused over the table & seating lounge
  const tableSpot = new THREE.SpotLight(0xffd580, 24.0, 28, 0.85, 0.4, 1.2);
  tableSpot.position.set(0, 5.0, 0);
  tableSpot.target = tableTop;
  phGroup.add(tableSpot);
  phGroup.add(tableSpot.target);

  // Soft Front Warm Fill for exterior camera visibility & warm glow through glass
  const frontWarmFill = new THREE.PointLight(0xfff3d6, 12.0, 22, 1.1);
  frontWarmFill.position.set(0, -1.5, 3.5);
  phGroup.add(frontWarmFill);

  scene.add(phGroup);

  // Animation hook for avatar gestures, coffee steam & calm lighting (no flicker)
  function update(t, low) {
    if (low) return;

    // Steam gentle drift only - constant opacity so no light flicker through the glass
    steamMesh.position.y = -4.3 + Math.sin(t * 1.5) * 0.03;
    steamMat.opacity = 0.4;

    // Table lamp warm light - constant, calm glow (removed breathing flicker)
    tableLampLight.intensity = 4.2;

    // Natural talking / gesturing animations for the faceless executive avatar
    if (avatar && avatar.userData) {
      const { headGroup, armR, forearmR, armL } = avatar.userData;

      // Conversational head movement (nodding + subtle turning with speech cadence)
      if (headGroup) {
        headGroup.rotation.x =
          Math.sin(t * 2.8) * 0.08 + Math.sin(t * 1.4) * 0.04;
        headGroup.rotation.y = Math.sin(t * 1.6) * 0.12;
        headGroup.rotation.z = Math.sin(t * 1.1) * 0.03;
      }

      // Expressive right arm / hand gesturing while explaining/talking
      if (armR) {
        armR.rotation.x = 0.65 + Math.sin(t * 2.2) * 0.15;
        armR.rotation.y = 0.2 + Math.cos(t * 1.8) * 0.12;
        armR.rotation.z = -0.25 + Math.sin(t * 2.5) * 0.08;
      }
      if (forearmR) {
        forearmR.rotation.x = -1.1 + Math.sin(t * 2.2 + 0.4) * 0.22;
        forearmR.rotation.z = 0.4 + Math.cos(t * 2.0) * 0.14;
      }

      // Subtle resting shift for left arm
      if (armL) {
        armL.rotation.x = 0.75 + Math.sin(t * 0.8) * 0.03;
      }
    }
  }

  return {
    phGroup,
    warmCeilingLight,
    tableSpot,
    update,
  };
}
